import React, { useEffect, useState, useCallback, useRef } from 'react';
import { fetchDevice, fetchDevices, fetchDeviceRecords, fetchFiles, fetchSharedWithMe, fetchSharedFiles, isAuthenticated } from './services/api';
import socket from './services/websocket';
import WaveFormViewer from './components/WaveFormViewer';
import DeviceList from './components/DeviceList';
import FileExplorer from './components/FileExplorer';
import Comments from './components/Comments';
import Sharing from './components/Sharing';
import Login from './components/Login';
import LocationMap from './components/LocationMap';

function App() {
  const authed = isAuthenticated();
  const [devices, setDevices] = useState([]);
  const [deviceNameById, setDeviceNameById] = useState({});
  const [files, setFiles] = useState([]);
  const [sharedFiles, setSharedFiles] = useState([]);
  const [sharerNameById, setSharerNameById] = useState({});
  const [audioBuffer, setAudioBuffer] = useState([]);
  const [streamedChunks, setStreamedChunks] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  // Raw audio for the waveform is a high-rate byte STREAM. Routing it through React
  // state dropped chunks (React coalesces rapid setState calls, keeping only the last,
  // which corrupts the accumulated fMP4 the waveform decodes) and re-rendered the whole
  // app on every chunk. Instead we hand chunks straight to WaveFormViewer through a
  // registered sink (a ref) - lossless and without any re-render.
  const rawAudioSinkRef = useRef(null);
  const handleRawAudioData = useCallback((payload) => {
    if (rawAudioSinkRef.current) {
      rawAudioSinkRef.current(payload);
    }
  }, []);
  const registerRawAudioSink = useCallback((fn) => {
    rawAudioSinkRef.current = fn;
  }, []);
  const [selectedFile, setSelectedFile] = useState(null); // Track if an audio file has been selected
  const [selectedTimeFrom, setSelectedTimeFrom] = useState(null); // Track selected time from WaveFormViewer
  const [selectedTimeTo, setSelectedTimeTo] = useState(null); // Track selected time to WaveFormViewer
  const [comments, setComments] = useState([]); // Track comments for waveform markers
  const [websocketCommentOutdated, setWebsocketCommentOutdated] = useState(false); // Track if comments are outdated via websocket
  const [highlightedCommentUuid, setHighlightedCommentUuid] = useState(null); // Track highlighted comment
  const [activeTab, setActiveTab] = useState('files'); // 'devices' | 'files' | 'waveform' | 'sharing' | 'settings'
  // DOM node above the waveform where FileExplorer portals the selected file's info card.
  const [waveformHeaderEl, setWaveformHeaderEl] = useState(null);

  // Track a narrow (phone) viewport so the top navigation can tighten its spacing
  // and wrap onto multiple lines instead of overflowing horizontally off-screen.
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 640px)').matches
      : false
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 640px)');
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Fetch devices on mount
  const fetchDevicesData = useCallback(async () => {
    try {
      const res = await fetchDevices();
      console.log('Devices fetched:', res);
        
      // Fetch details for each device sequentially
      const devicesWithDetails = await Promise.all(
        res.data.map(async (device) => {
          try {
            const deviceRes = await fetchDevice(device.uuid);
            //console.log('Device details fetched:', deviceRes);
            // Merge device list data with detailed properties
            return {
              ...device,
              ...deviceRes.data
            };
          } catch (err) {
            console.error(`Error fetching device ${device.uuid}:`, err);
            // Return original device data if details fetch fails
            return device;
          }
        })
      );
        
      // Devices shared WITH me (as grantee) arrive in the same /devices response
      // (the service authorizes them); tag them here via their denormalized uuid
      // so the card can show a shared indicator. Their record-id -> name mapping
      // and the sharer display name also come from the share rows, since a
      // grantee cannot read the devices collection directly.
      let sharedUsernames = new Set();
      let sharedNameById = {};
      let sharerNames = {};
      try {
        const { deviceShares, fileShares } = await fetchSharedWithMe();
        const rememberSharer = (r) => {
          const s = r?.expand?.sharer;
          if (r.sharer) sharerNames[r.sharer] = {
            name: s?.username || s?.name || s?.email || r.sharer,
            email: s?.email || '',
          };
        };
        deviceShares.forEach((r) => {
          if (r.deviceUsername) sharedUsernames.add(r.deviceUsername);
          if (r.device) sharedNameById[r.device] = r.deviceName || r.device;
          rememberSharer(r);
        });
        fileShares.forEach((r) => {
          if (r.device && !sharedNameById[r.device]) sharedNameById[r.device] = r.deviceName || r.device;
          rememberSharer(r);
        });
        setSharerNameById(sharerNames);
      } catch (shareErr) {
        console.error('Error loading received shares:', shareErr);
      }

      // The file tree groups blobs by "<userId>/<deviceId>/...", where deviceId is
      // the PocketBase device record id. The friendly name lives in the service
      // device properties keyed by username (uuid), so bridge record-id -> username
      // -> name to label the device folders. Shared devices' names come from the
      // share rows (merged in below). The owned-username set built here is also
      // used to drop stale devices from the list (see below).
      let ownedUsernames = null; // null => records fetch failed, don't filter
      try {
        const records = await fetchDeviceRecords();
        const nameByUsername = {};
        devicesWithDetails.forEach((d) => {
          if (d && d.uuid) nameByUsername[d.uuid] = d?.properties?.name || d.uuid;
        });
        const idToName = { ...sharedNameById };
        ownedUsernames = new Set();
        (records.items || []).forEach((rec) => {
          if (rec && rec.id) idToName[rec.id] = nameByUsername[rec.username] || rec.username || rec.id;
          if (rec && rec.username) ownedUsernames.add(rec.username);
        });
        setDeviceNameById(idToName);
      } catch (mapErr) {
        console.error('Error building device name map:', mapErr);
      }

      // The service's share-grant cache can briefly keep returning a device
      // after its share was revoked. Only keep devices we actually own or that
      // are currently shared with us (per fresh PocketBase data) so a revoked
      // device disappears immediately instead of lingering as editable. If the
      // records fetch failed we skip filtering to avoid hiding owned devices.
      const isKept = (d) =>
        ownedUsernames === null || ownedUsernames.has(d.uuid) || sharedUsernames.has(d.uuid);
      setDevices(
        devicesWithDetails
          .filter(isKept)
          .map((d) => ({ ...d, shared: sharedUsernames.has(d.uuid) && !(ownedUsernames && ownedUsernames.has(d.uuid)) }))
      );
    } catch (err) {
      console.error('Error fetching devices:', err);
    }
  }, []);

  
  // Initial fetch of devices
  useEffect(() => {
    if (!authed) return;
    fetchDevicesData();
  }, [authed, fetchDevicesData]);
  

  // Fetch files for a default folder (or based on user selection)
  const fetchFilesData = useCallback(() => {
    fetchFiles('')
      .then((res) => {
        //console.log('Files fetched:', res);
        setFiles(prevFiles => {
          // Only update if files have actually changed
          if (JSON.stringify(prevFiles) !== JSON.stringify(res.data)) {
            return res.data;
          }
          return prevFiles;
        });
      })
      .catch((err) => console.error('Error fetching files:', err));
  }, []);

  // Files shared WITH me are fetched separately (they live under other users'
  // namespaces), then merged into the same file tree.
  const fetchSharedFilesData = useCallback(() => {
    fetchSharedFiles()
      .then((items) => setSharedFiles(items))
      .catch((err) => console.error('Error fetching shared files:', err));
  }, []);

  // Manual refresh for the Streams tab (owned + shared), no page reload.
  const refreshStreams = useCallback(() => {
    fetchFilesData();
    fetchSharedFilesData();
  }, [fetchFilesData, fetchSharedFilesData]);

  useEffect(() => {
    if (!authed) return;

    // Initial fetch
    fetchFilesData();

    // Set up periodic fetching (every 2 seconds) : TODO work on push
    const interval = setInterval(fetchFilesData, 2000);

    return () => clearInterval(interval);
  }, [authed, fetchFilesData]);

  useEffect(() => {
    if (!authed) return;
    fetchSharedFilesData();
    const interval = setInterval(fetchSharedFilesData, 5000);
    return () => clearInterval(interval);
  }, [authed, fetchSharedFilesData]);

  // Handle incoming messages via WebSocket
  useEffect(() => {
    socket.onmessage = (event) => {
      console.log('WebSocket message received:', event.data);
      try {
        const parsed = JSON.parse(event.data);
        if (parsed.code && parsed.code === 301)
        {
          setWebsocketCommentOutdated({file: parsed.file, timestamp: Date.now()});
        }
        else if (parsed.code && parsed.code === 100) // device connected
        {
          fetchDevicesData();
        }
        else if (parsed.code && parsed.code === 101) // device disconnected
        {
          fetchDevicesData();
        }
        else if (parsed.code && parsed.code === 102) // device properties updated
        {
          fetchDevicesData();
        }
      } catch (e) {
        console.error('Failed to parse websocketCommentOutdated:', e);
      }
    };
  }, []);

  // Handle file selection
  const handleFileSelected = (fileName) => {
    setSelectedFile(fileName);
    // Selecting a recording jumps to its Waveform view; resize kicks a canvas redraw
    // now that the (previously hidden) waveform tab becomes visible.
    setActiveTab('waveform');
    setTimeout(() => window.dispatchEvent(new Event('resize')), 60);
  };

  // Handle time selection from WaveFormViewer clicks
  const handleTimeSelected = useCallback((timeInSeconds) => {
    // Convert seconds to hh:mm:ss.ms format
    const hours = Math.floor(timeInSeconds / 3600);
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const remainingSeconds = timeInSeconds % 60;
    
    const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toFixed(3).padStart(6, '0')}`;
    
    setSelectedTimeFrom(timeString);
    setSelectedTimeTo(timeString);
    console.log('Time selected from WaveFormViewer:', timeString);
  }, []);

  // Handle comments updates from Comments component
  const handleCommentsChange = useCallback((updatedComments) => {
    setComments(updatedComments);
    console.log('Comments updated:', updatedComments.length);
  }, []);

  // Store reference to waveform seek function
  const [waveformSeekFunction, setWaveformSeekFunction] = useState(null);
  // Live playback position (seconds) so the location map can highlight where
  // the recording was captured at the currently-playing moment.
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0);

  // Handle comment marker clicks
  const handleCommentClick = useCallback((comment) => {
    console.log('Comment clicked:', comment);
    setHighlightedCommentUuid(comment.uuid);
    
    // Set the time fields to the comment's time
    setSelectedTimeFrom(comment.timeFrom);
    setSelectedTimeTo(comment.timeTo || comment.timeFrom);
    
    // Seek audio playback to comment's timeFrom position using WaveFormViewer's functionality
    if (comment.timeFrom && waveformSeekFunction) {
      const timeInSeconds = timeStringToSeconds(comment.timeFrom);
      console.log(`Seeking to comment time: ${comment.timeFrom} (${timeInSeconds}s)`);
      
      const success = waveformSeekFunction(timeInSeconds);
      if (!success) {
        console.warn('Failed to seek using WaveFormViewer method');
      }
    }
  }, [waveformSeekFunction]);

  // Handle current time updates from WaveFormViewer
  const handleCurrentTimeChange = useCallback((currentTime, seekFunction) => {
    // Store the seek function for use in comment clicks
    setWaveformSeekFunction(() => seekFunction);
    // Feed the live playhead position to the location map.
    setCurrentPlaybackTime(currentTime);
  }, []);

  // Helper function to convert hh:mm:ss.ms to seconds (for audio seeking)
  const timeStringToSeconds = (timeString) => {
    if (!timeString || timeString === '') return 0;
    
    const parts = timeString.split(':');
    if (parts.length !== 3) return 0;
    
    const hours = parseInt(parts[0]) || 0;
    const minutes = parseInt(parts[1]) || 0;
    const secondsAndMs = parseFloat(parts[2]) || 0;
    
    return hours * 3600 + minutes * 60 + secondsAndMs;
  };

  return (
    <div style={{ backgroundColor: '#f0f0f2', minHeight: '100vh' }}>
      <header style={{
        backgroundColor: '#1a1a1a',
        color: 'white',
        padding: '0.85rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        borderBottom: '2px solid #d4af37'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#ffffff' }}>Project Chronos</h1>
          <div style={{ fontSize: '0.65rem', color: '#d4af37', letterSpacing: '0.14em', marginTop: '1px' }}>WIR WERDEN WIE GOLD SEIN</div>
        </div>
      </header>

      {/* Navigation Menu */}
      <nav style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: isNarrow ? '0.5rem 1rem' : '0 1.5rem',
        marginBottom: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.5rem',
        minHeight: '52px'
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="sennheiserLogo.svg" 
            alt="Sennheiser Logo" 
            style={{ 
              height: '22px', 
              width: 'auto'
            }}
          />
        </div>

        {/* Menu Items */}
        <div style={{ 
          display: 'flex', 
          gap: isNarrow ? '1rem' : '2rem',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          rowGap: '0.35rem'
        }}>
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('devices');
            }}
            style={{ 
              textDecoration: 'none', 
              color: activeTab === 'devices' ? '#d4af37' : '#495057',
              fontSize: isNarrow ? '0.9rem' : '1.0rem',
              fontWeight: '500',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#d4af37'}
            onMouseOut={(e) => e.target.style.color = activeTab === 'devices' ? '#d4af37' : '#495057'}
          >
            🎤Devices
          </a>
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('files');
            }}
            style={{ 
              textDecoration: 'none', 
              color: activeTab === 'files' ? '#d4af37' : '#495057',
              fontSize: isNarrow ? '0.9rem' : '1.0rem',
              fontWeight: '500',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#d4af37'}
            onMouseOut={(e) => e.target.style.color = activeTab === 'files' ? '#d4af37' : '#495057'}
          >
            📁Files
          </a>
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('waveform');
              setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
            }}
            style={{ 
              textDecoration: 'none', 
              color: activeTab === 'waveform' ? '#d4af37' : '#495057',
              fontSize: isNarrow ? '0.9rem' : '1.0rem',
              fontWeight: '500',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#d4af37'}
            onMouseOut={(e) => e.target.style.color = activeTab === 'waveform' ? '#d4af37' : '#495057'}
          >
            📈Waveform
          </a>
          <a 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('sharing');
            }}
            style={{ 
              textDecoration: 'none', 
              color: activeTab === 'sharing' ? '#d4af37' : '#495057',
              fontSize: isNarrow ? '0.9rem' : '1.0rem',
              fontWeight: '500',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#d4af37'}
            onMouseOut={(e) => e.target.style.color = activeTab === 'sharing' ? '#d4af37' : '#495057'}
          >
            🔗Sharing
          </a>
          {/* <a
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('settings');
            }}
            style={{ 
              textDecoration: 'none', 
              color: activeTab === 'settings' ? '#d4af37' : '#495057',
              fontSize: '1.0rem',
              fontWeight: '500',
              transition: 'color 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.color = '#d4af37'}
            onMouseOut={(e) => e.target.style.color = activeTab === 'settings' ? '#d4af37' : '#495057'}
          >
            ⚙️Settings
          </a> */}
          <Login onAuthChange={() => window.location.reload()} />
        </div>
      </nav>

      {authed ? (
      /* Main Layout Container */
      <div style={{
        display: 'flex',
        gap: '1.5rem',
        alignItems: 'flex-start',
        padding: '1.5rem'
      }}>
        {/* Left Column - Main Content */}
        <div style={{
          flex: '1',
          minWidth: '0' // Prevents flex child from overflowing
        }}>
          {/* Devices Tab */}
          {activeTab === 'devices' && (
            <section style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                <button
                  onClick={fetchDevicesData}
                  title="Refresh the device list"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 12px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                >
                  🔄 Refresh
                </button>
              </div>
              <DeviceList devices={devices} />
            </section>
          )}

          {/* Files Tab - always mounted to preserve the FileExplorer streaming / MediaSource state */}
          <div style={{ display: activeTab === 'files' ? 'block' : 'none' }}>
            <section style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                <button
                  onClick={refreshStreams}
                  title="Refresh the file list"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '5px 12px', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer' }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                >
                  🔄 Refresh
                </button>
              </div>
              <FileExplorer 
                files={[...files, ...sharedFiles]} 
                deviceNameById={deviceNameById}
                sharerNameById={sharerNameById}
                onStreamedChunks={setStreamedChunks}
                onStreamingStateChange={setIsStreaming}
                onAudioUrl={setAudioUrl}
                onRawAudioData={handleRawAudioData}
                onFileSelected={handleFileSelected}
                selectedInfoPortal={waveformHeaderEl}
              />
            </section>
          </div>

          {/* Waveform Tab - always mounted to preserve AudioContext, MediaSource, and waveform state */}
          <div style={{ display: activeTab === 'waveform' ? 'block' : 'none' }}>
            {selectedFile ? (
              <>
                {/* Selected file info card (portaled in from FileExplorer, live progress) */}
                <div ref={setWaveformHeaderEl} />

                {/* Waveform viewer */}
                <section style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: '1rem' }}>
                  <WaveFormViewer 
                    audioBuffer={audioBuffer}
                    streamedChunks={streamedChunks}
                    isStreaming={isStreaming}
                    audioUrl={audioUrl}
                    registerRawAudioSink={registerRawAudioSink}
                    onTimeSelected={handleTimeSelected}
                    comments={comments}
                    onCommentClick={handleCommentClick}
                    highlightedCommentUuid={highlightedCommentUuid}
                    onCurrentTimeChange={handleCurrentTimeChange}
                  />
                </section>

                {/* Location breadcrumb map */}
                <section style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <LocationMap selectedFile={selectedFile} currentTime={currentPlaybackTime} />
                </section>
              </>
            ) : (
              <section style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '2.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                <h2 style={{ color: '#1a1a1a', marginTop: 0, fontSize: '1rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Waveform</h2>
                <p style={{ color: '#9ca3af' }}>Select a recording in the <strong>Files</strong> tab to view its waveform, comments and location.</p>
              </section>
            )}
          </div>
          {/* Sharing Tab */}
          {activeTab === 'sharing' && (
            <section style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <Sharing deviceNameById={deviceNameById} />
            </section>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <section style={{ backgroundColor: 'white', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '2.5rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center' }}>
              <h2 style={{ color: '#1a1a1a', marginTop: 0, fontSize: '1rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Settings</h2>
              <p style={{ color: '#9ca3af' }}>Settings panel coming soon...</p>
            </section>
          )}
        </div>

        {/* Right Column - Comments Panel (only shown when file is selected and on the Waveform tab) */}
        {selectedFile && activeTab === 'waveform' && (
          <div style={{
            width: '350px',
            minHeight: '600px',
            flexShrink: 0
          }}>
            <Comments 
              selectedFile={selectedFile} 
              selectedTimeFrom={selectedTimeFrom}
              selectedTimeTo={selectedTimeTo}
              onCommentsChange={handleCommentsChange}
              highlightedCommentUuid={highlightedCommentUuid}
              onCommentClick={handleCommentClick}
              websocketCommentOutdated={websocketCommentOutdated}
            />
          </div>
        )}
      </div>
      ) : (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '4rem 1.5rem',
        }}>
          <section style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            border: '1px solid #e5e7eb',
            padding: '2.5rem',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            textAlign: 'center',
            maxWidth: '420px',
          }}>
            <h2 style={{ color: '#1a1a1a', marginTop: 0, fontSize: '1rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sign in required</h2>
            <p style={{ color: '#9ca3af' }}>Please sign in with the <strong>Login</strong> button in the top-right menu to view devices and files.</p>
          </section>
        </div>
      )}
    </div>
  );
}

export default App;
